/**
 * De kop, de voet en de testbalk om de publieke pagina's heen.
 */

import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Kroon } from './basis';
import type { OmgevingInfo } from '../api';
import { STICHTING } from '../lib/teksten';

export function Kop({ omgeving }: { omgeving: OmgevingInfo }) {
  return (
    <header className="mx-auto mt-24 flex max-w-6xl items-center justify-between gap-4 px-4 pt-5 pb-2 sm:px-6">
      <Link to="/" className="flex items-center gap-3 text-inkt no-underline">
        <Kroon className="h-8 w-8 text-goud-400" />
        <span className="sr-only font-display text-lg leading-tight sm:not-sr-only sm:text-xl">
          <span className="text-groep-bruin">Helden</span> <span className="text-[#222]">van de</span> <span className="text-groep-groen">Hilfertheem</span>
        </span>
      </Link>
      <nav className="flex items-center gap-1 text-sm sm:gap-2">
        <NavLink
          to="/mijn"
          className={({ isActive }) =>
            `rounded-full px-3 py-1.5 no-underline transition ${isActive ? 'text-goud-300' : 'text-inkt-zacht hover:text-inkt'}`
          }
        >
          {omgeving.heldIngelogd ? 'Mijn pagina' : 'Inloggen'}
        </NavLink>
        <Link to="/word-held" className="knop knop-goud knop-klein">
          Word held
        </Link>
      </nav>
    </header>
  );
}

/**
 * De voet van de groepssite (besluit 28 oktober 2026), zoals de menubalk:
 * het gras boven een donkergroen vlak, de sociale media van de groep, en
 * daaronder onze eigen teksten en links in wit.
 */
export function Voet() {
  return (
    <footer className="groepsvoet mt-12">
      <div className="bg-groep-groen text-white">
        <div className="mx-auto grid max-w-6xl gap-x-8 px-4 pt-4 pb-10 sm:px-6 lg:grid-cols-2">
          <div>
            <ul className="flex gap-4">
              <li>
                <a href="https://www.facebook.com/Hilfertheem" title="Bekijk ons op Facebook" className="block text-white hover:opacity-80">
                  <Facebook />
                  <span className="sr-only">Facebook</span>
                </a>
              </li>
              <li>
                <a href="https://www.instagram.com/hilfertheem/" title="Bekijk ons op Instagram" className="block text-white hover:opacity-80">
                  <Instagram />
                  <span className="sr-only">Instagram</span>
                </a>
              </li>
            </ul>
            <div className="py-8 text-sm leading-relaxed font-light">
              <p>
                Helden van de Hilfertheem is een initiatief van de {STICHTING.naam}, {STICHTING.plaats}. KvK {STICHTING.kvk}.
              </p>
              <p className="mt-2">{STICHTING.adres}.</p>
              <p className="mt-2">
                Vragen? Mail{' '}
                <a href={`mailto:${STICHTING.email}`} className="text-white underline underline-offset-2 hover:opacity-80">
                  {STICHTING.email}
                </a>
                .
              </p>
            </div>
          </div>
          <nav className="flex flex-wrap content-start gap-x-6 gap-y-2 pb-8 font-light lg:justify-end lg:pt-1">
            <Link to="/word-held" className="text-white hover:opacity-80">
              Word held
            </Link>
            <Link to="/voorwaarden" className="text-white hover:opacity-80">
              Voorwaarden
            </Link>
            <Link to="/privacy" className="text-white hover:opacity-80">
              Privacy
            </Link>
            <Link to="/beheer" className="text-white/70 hover:opacity-80">
              Beheer
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}

function Facebook() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <rect width="24" height="24" rx="3" fill="currentColor" />
      <path
        fill="var(--color-groep-groen)"
        d="M16.6 24v-9.3h3.1l.5-3.6h-3.6V8.8c0-1 .3-1.8 1.8-1.8h1.9V3.8c-.3 0-1.5-.1-2.8-.1-2.8 0-4.6 1.7-4.6 4.8v2.6H9.8v3.6h3.1V24z"
      />
    </svg>
  );
}

function Instagram() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2">
      <rect x="1.6" y="1.6" width="20.8" height="20.8" rx="5.5" />
      <circle cx="12" cy="12" r="4.8" />
      <circle cx="17.9" cy="6.1" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function Testbalk({ omgeving }: { omgeving: OmgevingInfo }) {
  if (!omgeving.testmodus) return null;
  return (
    <div className="border-b border-test/30 bg-test/10 px-4 py-1.5 text-center text-xs text-test">
      Testomgeving - er gaat geen echte post uit en er wordt geen echt geld geïnd
      {omgeving.klok?.verzet ? ` · de klok staat op ${omgeving.klok.datum}` : ''}
    </div>
  );
}

export function Pagina({ children, smal = false }: { children: ReactNode; smal?: boolean }) {
  return <main className={`mx-auto px-4 sm:px-6 ${smal ? 'max-w-2xl' : 'max-w-6xl'}`}>{children}</main>;
}
